import { Capacitor } from '@capacitor/core';
import { addDays, today } from './dates';
import { clock, deviceZone, localDate } from './schedule';

// Reminders the iPhone app schedules itself (there is no server push yet): before each appointment with the
// coach, "did you train?" 15 minutes after it (Đã tập / Không tập, which the coach sees), and lunch/dinner logging. Every sync cancels the ones it
// made before (ids in its own range) and schedules them again from the server's appointments and the
// account's settings, so it is safe to run as often as needed. On the website it does nothing.
const BASE_ID = 100000;
const ID_RANGE = 100000;
const DAYS_AHEAD = 14;
// iOS keeps at most 64 pending local notifications per app.
const MAX_PENDING = 60;
const SYNC_EVENT = 'macro-mate:reminders-sync';
export const APPOINTMENT_ACTIONS = 'appointment';
export const CHECKIN_ACTIONS = 'checkin';
// The "did you train?" question comes this long after the session ends.
const CHECKIN_AFTER_MIN = 15;

export const remindersSupported = () => Capacitor.isNativePlatform();
// Asks the app's ReminderSync to sync again (after anything that changes what is due).
export const requestReminderSync = () => window.dispatchEvent(new Event(SYNC_EVENT));
export const onReminderSyncRequest = (fn) => {
  window.addEventListener(SYNC_EVENT, fn);
  return () => window.removeEventListener(SYNC_EVENT, fn);
};

async function plugin() {
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  return LocalNotifications;
}

// 'granted' | 'denied' | 'prompt' | 'unsupported'. `ask` shows the iPhone's permission question when it can.
export async function reminderPermission({ ask = false } = {}) {
  if (!remindersSupported()) return 'unsupported';
  const notifications = await plugin();
  let { display } = await notifications.checkPermissions();
  if (ask && display.startsWith('prompt')) ({ display } = await notifications.requestPermissions());
  return display.startsWith('prompt') ? 'prompt' : display;
}

export async function registerReminderActions() {
  if (!remindersSupported()) return;
  const notifications = await plugin();
  // `foreground`: each button opens the app, which then does what it says.
  await notifications.registerActionTypes({
    types: [{
      id: APPOINTMENT_ACTIONS,
      actions: [{ id: 'view', title: 'Xem lịch', foreground: true }, { id: 'reschedule', title: 'Xin đổi giờ', foreground: true },
        { id: 'skip', title: 'Nghỉ hôm nay', foreground: true }],
    }, {
      id: CHECKIN_ACTIONS,
      actions: [{ id: 'trained', title: 'Đã tập', foreground: true }, { id: 'missed', title: 'Không tập', foreground: true }],
    }],
  });
}

export async function onReminderAction(fn) {
  if (!remindersSupported()) return () => {};
  const notifications = await plugin();
  const handle = await notifications.addListener('localNotificationActionPerformed', ({ actionId, notification }) => fn(actionId, notification.extra || {}));
  return () => handle.remove();
}

// A stable id per reminder, so the same reminder keeps its id across syncs.
const reminderId = (key) => {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) | 0;
  return BASE_ID + (Math.abs(h) % ID_RANGE);
};

const minutesOf = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};
// Quiet hours can run past midnight (22:30–06:30).
const inQuietHours = (date, settings) => {
  if (!settings.quiet) return false;
  const t = date.getHours() * 60 + date.getMinutes();
  const from = minutesOf(settings.quiet_from);
  const to = minutesOf(settings.quiet_to);
  return from <= to ? t >= from && t < to : t >= from || t < to;
};
const at = (isoDate, hhmm) => {
  const [y, mo, d] = isoDate.split('-').map(Number);
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(y, mo - 1, d, h, m);
};
const LEAD_TEXT = { 15: '15 phút', 30: '30 phút', 60: '1 tiếng', 120: '2 tiếng' };
// The first moment at or after `date` outside quiet hours.
const afterQuiet = (date, settings) => {
  if (!inQuietHours(date, settings)) return date;
  const [h, m] = settings.quiet_to.split(':').map(Number);
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m);
  if (end <= date) end.setDate(end.getDate() + 1);
  return end;
};

// What should be pending now, from the account's settings, the next two weeks of appointments and today's log.
export function plannedReminders({ settings, appointments, todayLog, now = new Date() }) {
  const list = [];
  const logged = { workout: (todayLog?.activities || []).length > 0, meals: new Set((todayLog?.meals || []).map((m) => m.meal_type)) };
  const todayIso = today();
  const live = appointments.filter((a) => a.state !== 'absent' && a.state !== 'past');

  if (settings.appointments) {
    live.forEach((a) => {
      const when = new Date(new Date(a.starts_at).getTime() - settings.lead_minutes * 60000);
      if (when <= now) return;
      list.push({
        id: reminderId(`appt:${a.id}`), at: when, actionTypeId: APPOINTMENT_ACTIONS,
        title: `${LEAD_TEXT[settings.lead_minutes] || `${settings.lead_minutes} phút`} nữa tập với Coach ${a.coach_name}`,
        body: `${clock(a.starts_at)}${a.location ? ` tại ${a.location}` : ''}${a.note ? ` · ${a.note}` : ''}. Ăn nhẹ trước khi tập nhé.`,
        extra: { kind: 'appointment', appointment_id: a.id, date: localDate(a.starts_at) },
      });
    });
  }
  // "Did you train?" after each session not answered yet; in quiet hours it waits until they end.
  if (settings.forgot) {
    appointments.filter((a) => a.state !== 'absent' && !a.attendance).forEach((a) => {
      const date = localDate(a.starts_at);
      const ends = new Date(new Date(a.ends_at).getTime() + CHECKIN_AFTER_MIN * 60000);
      const when = afterQuiet(ends, settings);
      if (when <= now || (date === todayIso && logged.workout)) return;
      const later = localDate(when.toISOString()) !== date;
      list.push({
        id: reminderId(`checkin:${a.id}`), at: when, actionTypeId: CHECKIN_ACTIONS,
        title: `Buổi tập ${clock(a.starts_at)}${later ? ' hôm qua' : ''} với Coach ${a.coach_name} xong chưa?`,
        body: 'Bấm Đã tập hoặc Không tập để Coach biết.', extra: { kind: 'checkin', appointment_id: a.id, date },
      });
    });
  }
  if (settings.meals) {
    for (let i = 0; i < 7; i += 1) {
      const date = addDays(todayIso, i);
      [['lunch', '12:00', 'Bữa trưa'], ['dinner', '19:30', 'Bữa tối']].forEach(([type, time, label]) => {
        const when = at(date, time);
        if (when <= now || inQuietHours(when, settings) || (i === 0 && logged.meals.has(type))) return;
        list.push({
          id: reminderId(`meal:${type}:${date}`), at: when, title: `${label} hôm nay ăn gì?`,
          body: 'Chụp ảnh hoặc mô tả món, AI tính calo giúp bạn.', extra: { kind: 'meal', meal_type: type, date },
        });
      });
    }
  }
  return list.sort((x, y) => x.at - y.at).slice(0, MAX_PENDING);
}

// Replaces this app's pending reminders with what is due now. Without permission it leaves them alone.
export async function syncReminders(api, settings) {
  if (!remindersSupported() || !settings) return;
  const notifications = await plugin();
  if ((await notifications.checkPermissions()).display !== 'granted') return;

  // From yesterday: a late session's "did you train?" can wait until quiet hours end the next morning.
  const from = addDays(today(), -1);
  const needAppointments = settings.appointments || settings.forgot;
  const [schedule, todayLog] = await Promise.all([
    needAppointments
      ? api.get(`/appointments?from=${from}&to=${addDays(from, DAYS_AHEAD)}&time_zone=${encodeURIComponent(deviceZone())}`)
      : Promise.resolve({ appointments: [] }),
    api.get(`/days/${today()}`).catch(() => null),
  ]);
  const wanted = plannedReminders({ settings, appointments: schedule.appointments || [], todayLog });

  const { notifications: pending } = await notifications.getPending();
  const ours = pending.filter((n) => n.id >= BASE_ID && n.id < BASE_ID + ID_RANGE);
  if (ours.length) await notifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
  if (wanted.length) {
    await notifications.schedule({
      notifications: wanted.map((n) => ({
        id: n.id, title: n.title, body: n.body, extra: n.extra, actionTypeId: n.actionTypeId,
        schedule: { at: n.at, allowWhileIdle: true },
      })),
    });
  }
}

// Where a tapped reminder (or one of its buttons) opens.
export function reminderTarget(actionId, extra) {
  const date = extra.date || today();
  const onDay = date === today() ? '' : `&date=${date}`;
  if (extra.kind === 'meal') return `/day?tab=meals${onDay}`;
  // A "did you train?" reminder (its buttons answered first, in ReminderSync) opens that day's Tập luyện.
  if (extra.kind === 'appointment' && actionId === 'view') return `/schedule?date=${date}`;
  if (extra.kind === 'appointment' && actionId === 'reschedule') return `/schedule?date=${date}&appointment=${extra.appointment_id}&sheet=reschedule`;
  if (extra.kind === 'appointment' && actionId === 'skip') return `/schedule?date=${date}&appointment=${extra.appointment_id}&sheet=absence`;
  return `/day?tab=workout&from=reminder${onDay}`;
}
